import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import {
  DesignFirmType,
  Permissions,
  TeamRole,
  Workspace,
} from "ts-interfaces";
import { firebaseHelper, useWorkspaceContext } from "ui";
import { isTeamPlanActive } from "ui/utils/team/teamRole";
import { PERSONAL_LABEL, TEAM_FALLBACK_LABEL } from "./workspaceStrings";

export const roleLabel = (role: TeamRole): string => {
  if (role === TeamRole.ADMIN) return "Admin";
  if (role === TeamRole.DESIGNER_AND_ADMIN) return "Designer & Admin";
  return "Designer";
};

/**
 * Data for the workspace switcher next to the logo in the Bar
 * (WorkspaceSwitcher). Wraps useWorkspaceContext and resolves human-friendly
 * firm names for every team the user has permission for — active AND
 * inactive, since the menu lists both (inactive ones as disabled rows with a
 * "contact your admin" hint).
 */
export const useWorkspaceMenu = (
  uid: string | null,
  permissions: Permissions | null | undefined,
) => {
  const ctx = useWorkspaceContext(uid, permissions);
  const [firmNames, setFirmNames] = useState<Record<string, string>>({});
  // From the same read: is the team's plan live? Absent until a doc is read.
  const [firmPlans, setFirmPlans] = useState<Record<string, boolean>>({});

  const teamFirmIds = useMemo(() => {
    const fromActive = ctx.workspaces
      .filter(
        (w): w is Extract<Workspace, { kind: "team" }> => w.kind === "team",
      )
      .map((w) => w.firmId);
    const all = new Set<string>([...fromActive, ...ctx.inactiveTeamIds]);
    // While permissions load, the active team is rebuilt from the stored id
    // and isn't in `workspaces` yet.
    if (ctx.active.kind === "team") all.add(ctx.active.firmId);
    return Array.from(all);
  }, [ctx.workspaces, ctx.inactiveTeamIds, ctx.active]);

  useEffect(() => {
    if (teamFirmIds.length === 0) return undefined;
    let cancelled = false;
    const load = async () => {
      const next: Record<string, string> = {};
      const plans: Record<string, boolean> = {};
      await Promise.all(
        teamFirmIds.map(async (id) => {
          if (firmNames[id]) {
            next[id] = firmNames[id];
            return;
          }
          // "" (no name, or a failed read) labels the row "Team", never the
          // raw firm id, and lets a later run try again.
          try {
            const snap = await getDoc(
              doc(firebaseHelper.db, "design_firms", id),
            );
            const data = snap.data() as DesignFirmType | undefined;
            next[id] = data?.name?.trim() || "";
            if (data) plans[id] = isTeamPlanActive(data);
          } catch {
            next[id] = "";
          }
        }),
      );
      if (!cancelled) {
        setFirmNames((prev) => ({ ...prev, ...next }));
        setFirmPlans((prev) => ({ ...prev, ...plans }));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // Only re-run when the set of team ids changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamFirmIds.join(",")]);

  const resolveFirmName = (firmId: string): string =>
    firmNames[firmId] || TEAM_FALLBACK_LABEL;

  const resolveLabel = (w: Workspace): string =>
    w.kind === "personal" ? PERSONAL_LABEL : resolveFirmName(w.firmId);

  // True once the read settled, failed reads included.
  const isFirmNameLoaded = (firmId: string): boolean => firmId in firmNames;

  // Undefined while loading or after a failed read.
  const teamPlanActive = (firmId: string): boolean | undefined =>
    firmPlans[firmId];

  return {
    ctx,
    resolveLabel,
    resolveFirmName,
    isFirmNameLoaded,
    teamPlanActive,
  };
};
